import {
  HelpdeskTicketInterface,
  TicketSupplierAssignment,
} from "shared/types/helpdesk";
import { Contact } from "screens/mass-mail";
import { resolveSupplierRecipientEmail } from "shared/utils/companyContact";

/**
 * Centrale helpers rond de leveranciers van een helpdeskticket.
 *
 * Sinds de multi-leverancier refactor heeft een ticket meerdere leveranciers via
 * `supplierAssignments` (koppeltabel `ticket_supplier_assignment`). De backend geeft
 * geen losse `ticket.supplier` meer terug. Gebruik deze helpers overal i.p.v.
 * rechtstreeks `ticket.supplier` te lezen.
 */

/** Alle leverancier-toewijzingen van een ticket, gesorteerd op sortOrder. */
export const getTicketSupplierAssignments = (
  ticket: Pick<HelpdeskTicketInterface, "supplierAssignments"> | null | undefined,
): TicketSupplierAssignment[] => {
  const assignments = ticket?.supplierAssignments ?? [];
  return [...assignments].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
};

/** Heeft het ticket minstens één leverancier? */
export const hasTicketSuppliers = (
  ticket: Pick<HelpdeskTicketInterface, "supplierAssignments"> | null | undefined,
): boolean => getTicketSupplierAssignments(ticket).length > 0;

/**
 * Primaire (eerste) leverancier van een ticket. Back-compat helper voor plekken
 * die nog één leverancier tonen (templates, intervention default, ...).
 */
export const getPrimaryTicketSupplier = (
  ticket: Pick<HelpdeskTicketInterface, "supplierAssignments"> | null | undefined,
): TicketSupplierAssignment["supplier"] | null => {
  const assignments = getTicketSupplierAssignments(ticket);
  return assignments.length ? assignments[0].supplier : null;
};

/** Komma-gescheiden namen van alle leveranciers (voor compacte weergave). */
export const getTicketSupplierNames = (
  ticket: Pick<HelpdeskTicketInterface, "supplierAssignments"> | null | undefined,
): string =>
  getTicketSupplierAssignments(ticket)
    .map((a) => a.supplier?.name)
    .filter(Boolean)
    .join(", ");

/**
 * Zet de leveranciers van een ticket om naar mass-mail Contacts. Optioneel
 * gefilterd op een set ContactCompany-id's (voor de multi-select bij verzenden).
 *
 * Is er bij een leverancier een **contactpersoon** gekozen, dan wordt diens naam
 * en adres gebruikt — met de firmanaam erbij zodat je nog ziet om welke
 * leverancier het gaat. De backend past dezelfde regel toe bij het effectief
 * verzenden (`resolveSupplierAssignmentEmail`); zonder dit zou de modal `info@…`
 * tonen terwijl de mail naar de contactpersoon vertrekt.
 */
export const ticketSuppliersToContacts = (
  ticket: HelpdeskTicketInterface,
  onlySupplierIds?: number[],
): Contact[] => {
  const idFilter = onlySupplierIds?.length ? new Set(onlySupplierIds) : null;
  return getTicketSupplierAssignments(ticket)
    .filter((a) => a.supplier && (!idFilter || idFilter.has(a.supplier.id)))
    .map((a) => {
      const supplier = a.supplier;
      const buildingName =
        (ticket.building as any)?.location?.formattedAddress ||
        ticket.building?.id ||
        "";

      const contactNaam = a.contact
        ? `${a.contact.firstName ?? ""} ${a.contact.lastName ?? ""}`.trim()
        : "";
      const email =
        resolveSupplierRecipientEmail(a.contact, supplier.email) || "";
      const supplierNaam = supplier.name || "Onbekende leverancier";
      const weergaveNaam = contactNaam
        ? `${contactNaam} (${supplierNaam})`
        : supplierNaam;

      return {
        id: String(supplier.id),
        name: weergaveNaam,
        firstName: weergaveNaam,
        lastName: "",
        email,
        resourceId: String(supplier.id),
        phoneMobile: a.contact?.phone_mobile || supplier.phone || "",
        units: [],
        buildingId: ticket.building?.id || "",
        buildingName,
        unitId: "",
        unitNumber: "",
      } as Contact;
    });
};
